'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * CollapsibleCard — a card with a click-to-toggle header.
 *
 * Built for the prescription wizard's deep modification sections: the form
 * spans rearfoot / midfoot / forefoot / offloadings, so per Rafael's UX
 * feedback we let podiatrists fold sections they're not editing to keep
 * the page scannable. Defaults to expanded; uncontrolled state with an
 * optional `defaultOpen` and a fully-controlled `open` + `onOpenChange`
 * mode for callers that want to remember per-section toggles in URL or
 * localStorage.
 *
 * The header renders as a button so keyboard / SR users can toggle with
 * Enter / Space. ChevronDown rotates 180° when open.
 */

interface BaseProps {
  title: React.ReactNode;
  /** Right-aligned content inside the header (e.g. a "1 selected" badge). */
  rightSlot?: React.ReactNode;
  /** Subtitle shown under the title, smaller + muted. */
  subtitle?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  children: React.ReactNode;
}

interface ControlledProps extends BaseProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  defaultOpen?: never;
}

interface UncontrolledProps extends BaseProps {
  defaultOpen?: boolean;
  open?: never;
  onOpenChange?: never;
}

export type CollapsibleCardProps = ControlledProps | UncontrolledProps;

export function CollapsibleCard(props: CollapsibleCardProps) {
  const {
    title,
    rightSlot,
    subtitle,
    className,
    bodyClassName,
    headerClassName,
    children,
  } = props;

  const isControlled = 'open' in props && typeof props.open === 'boolean';
  const [internalOpen, setInternalOpen] = React.useState<boolean>(
    isControlled ? !!props.open : (props as UncontrolledProps).defaultOpen ?? true,
  );
  const open = isControlled ? !!props.open : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) {
      props.onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };

  const contentId = React.useId();

  return (
    <div
      className={cn(
        'rounded-lg border border-border-subtle bg-surface-elevated shadow-sm',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-2.5 text-start',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
          'rounded-lg',
          headerClassName,
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text-primary leading-tight">{title}</div>
          {subtitle ? (
            <div className="text-xs text-text-muted mt-0.5">{subtitle}</div>
          ) : null}
        </div>
        {rightSlot ? (
          <div className="text-xs text-text-secondary shrink-0">{rightSlot}</div>
        ) : null}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 text-text-secondary transition-transform duration-200',
            open ? 'rotate-180' : '',
          )}
        />
      </button>
      {open ? (
        <div id={contentId} className={cn('px-4 pb-4 pt-1', bodyClassName)}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
